import { BaseApiClient } from './base.client';

/**
 * Shows (theatre) bookings — unlike hotels/sports/restaurants, there is no
 * shows-service in this monorepo. The booking API already exists on the CMS
 * (Wix Velo) domain at `${NEXT_PUBLIC_IMAGE_BASE_URL}/api/integration` and is
 * called directly from the client — see `services/hotels-service/CLAUDE.md`'s
 * Wix Velo runtime notes (`req.MemberSession`, response envelope
 * `{ status, message, data }`). The upstream handler resolves the member
 * purely from `req.MemberSession._id` (the CMS session cookie) — it does
 * NOT read a client-supplied memberId param — so every call here needs
 * `credentials: 'include'` and only works when the browser already carries
 * a valid CMS session cookie for this domain.
 */

export interface ShowBookingSeat {
  X: number;
  Y: number;
  Iid: string;
  Row: string;
  Seat: string;
  Tid: string;
  PB?: string;
  CC?: string;
  FP: number;
  SP: number;
  PrB: number;
  A: string;
  S: string;
  R: string;
  N: number;
  Pr?: string;
  AtId?: number;
  D?: string;
}

export interface ShowBasketTicket {
  TicketId: number;
  TicketName1: string;
  TicketName2: string;
  FaceValue: number;
  SellingPrice: number;
  IsRestrictedView: boolean;
  RestrictionDescription: string | null;
  Barcode: string | null;
  BarcodeType: string;
  ScheduledOn: string | null;
}

export interface ShowBasketItem {
  BasketItemId: number;
  EventId: number;
  EventName: string;
  ImageUrl: string;
  PerformanceDate: string;
  AreaName: string;
  Seats: string[];
  Tickets: ShowBasketTicket[];
  VenueId: number;
  VenueName: string;
  VenueAddress: string;
  VenueCity: string;
  VenuePostCode: string;
  PriceSubTotal: number;
  ReservationValidity: number;
  ExpirationDate: string;
}

export interface ShowBasketData {
  BasketId: string;
  MinExpirationDate: string;
  DeliveryInformation: unknown;
  Items: ShowBasketItem[];
  DiscountInformation: unknown;
}

export interface ShowDeliveryType {
  DeliveryTypeId: number;
  DeliveryTypeName: string;
  DeliveryTypePrice: number;
}

export interface ShowUserInfoDetails {
  firstName: string;
  lastName: string;
  email: string;
  address1?: string;
  address2?: string;
  city?: string;
  countryCode?: string;
  country?: string;
  phone?: string;
  booker?: {
    firstName: string;
    lastName: string;
    isMainGuest: boolean;
  };
}

export interface ShowVenueDetail {
  venueId: number;
  address: string;
  city: string;
  cityId?: string;
  country: string;
  countryCode: string;
  currencyCode: string;
  currencyName: string;
  currencySymbol: string;
  latitude?: number;
  longitude?: number;
  postcode: string;
  state?: string;
  theatreName: string;
}

export interface ShowPaymentTicket extends ShowBasketTicket {
  AreaName: string;
  originalTicketPrice: number;
  SellingPriceUserCurrency: string;
  priceBreakdownSummary?: Record<string, number>;
  TicketPriceCalculationSummary?: Record<string, string>;
}

export interface ShowTicketCalculationAmtSummary {
  totalTickets: number;
  netRate: string;
  totalBIPCommissionAmt: string;
  totalCostAmt: string;
  bookingAmtTheatreCurrency: number;
  bookingAmtUserCurrency: number;
  bipCommissionExTax: string;
  costExTax: string;
  totalPriceExTax: string;
  taxFromSource: string;
  bipCommissionRate: number;
  bipCommissionVatRate: number;
  bipCommissionTax: string;
  priceWithBipCommissionExTax: string;
  taxes: string;
  priceWithBipCommissionInclTax: string;
  costRate: number;
  costVatRate: number;
  vatOnCost: string;
  totalTax: string;
}

export interface ShowOrderSummaryData {
  Email: string;
  Name: string;
  LastName: string;
  AddressLine1?: string;
  AddressLine2?: string;
  CompanyName: string | null;
  City?: string;
  Country?: string;
  Zip: string | null;
  Phone?: string;
  Mobile?: string;
  DeliveryType: number;
  SendConfirmationEmail: boolean;
  TransactionReference: string | null;
  BasketId: string;
}

export interface ShowPaymentDetails {
  currency: string;
  TheatreCurrencyCode: string;
  eventName: string;
  venueDetail: ShowVenueDetail;
  userCurrency: string;
  conversionRate: number;
  theatreCurrenyconversionRate: number;
  MinExpirationDate: string;
  converTedPrice: number;
  totalSellingPriceTheatreCurrency: string;
  totalPrice: number;
  tickets: ShowPaymentTicket[];
  TicketCalculationAmtSummary: ShowTicketCalculationAmtSummary;
  paymentId: string;
  StripePayload?: { stripePaymentIntentId: string; bookingId: string };
  orderSummaryData: ShowOrderSummaryData;
}

export interface ShowETicketNotice {
  EventId: number;
  Message: string;
}

export interface ShowSpecialNoteData {
  CheckoutNotices: unknown[];
  ETicketNotices: ShowETicketNotice[];
  CustomerEmailIsMandatory: boolean;
}

export interface ShowDeliveryData {
  DeliveryTypes: ShowDeliveryType[];
}

/** One Shows (theatre) booking record, as returned by the CMS's
 *  `get-my-theater-bookings` / `get-booking-detail` endpoints. Field
 *  casing matches the upstream Wix Velo doc verbatim (PascalCase +
 *  camelCase mixed, per the source system) — do not re-shape it. */
export interface ShowBooking {
  _id: string;
  Status: string;
  /** Stringified JSON array of `ShowBookingSeat` — parse with JSON.parse. */
  body: string;
  IsActive: boolean;
  EventId: string;
  basketData: ShowBasketData;
  SeatplanURl?: string;
  CreatedDate: string;
  WebSiteID: string;
  createdAt: string;
  reservationId: string;
  source: string;
  BasketId: string;
  updatedAt: string;
  PerformanceEnd: string;
  PerformanceStart: string;
  memberId: string;
  /** Human-readable "Weekday, D Month YYYY - h:mm am/pm". */
  performanceDate: string;
  /** Stringified JSON — parsed shape matches `UserInfoDetails`. */
  UserDetail: string;
  UserInfoDetails: ShowUserInfoDetails;
  stripeCustomerId?: string;
  DeliveryType: number;
  PaymentStatus: string;
  deliveryData: ShowDeliveryData;
  paymentDetails: ShowPaymentDetails;
  /** Stringified JSON duplicate of `paymentDetails`. */
  paymentInfo: string;
  stripePaymentIntentId?: string;
  userCurrency: string;
  /** Stringified raw Stripe PaymentIntent JSON — display-only, not typed further. */
  PaymentResponse?: string;
  TheatrePaymentStatus?: string;
  paymentMethodId?: string;
  BookingReference: string;
  ConfirmedAt?: string;
  FailedReason?: string | null;
  ProcessingDelayEmailStatus?: string;
  SendConfirmationEmail?: string;
  /** Raw supplier (LTD) basket confirmation payload — display/audit only. */
  submittedBasketRawResponse?: Record<string, any>;
  specialNoteData?: ShowSpecialNoteData;
  /** CMS `d.eventRunningTime` — free-text show duration, e.g. "2 hours 30 minutes (incl.
   *  20 minute interval)". Optional — the detail page's "Run Time" row only renders when set. */
  eventRunningTime?: string;
}

/** One row in the manage-bookings "Shows" tab. */
export interface ShowsBookingListItem extends ShowBooking {
  bookingType: 'shows';
}

export interface ShowsBookingsListResponse {
  status: number;
  message: string;
  data: { Records: ShowBooking[]; TotalCount: number; totalPages: number };
}

/** One ticket row as returned by the CMS's `get-booking-detail` endpoint — a fresh live LTD basket
 *  fetch, so `barcode` carries a real (or sandbox "FAKE...") value, unlike the list endpoint's raw
 *  `Barcode: null`. */
export interface ShowDetailTicket {
  area: string;
  TicketId: number;
  seat: string;
  barcode: string | null;
  Price: string;
  barcodeType: string;
}

/** Clean, pre-shaped booking detail, as returned by the CMS's `get-booking-detail` endpoint
 *  (gated only by `cartId`, no member session) — a different, much simpler shape than the list
 *  endpoint's raw order doc (`ShowBooking`). Field casing matches the upstream response verbatim. */
export interface ShowBookingDetailData {
  orderId: string;
  bookingReference: string;
  reservationId: string;
  bookingDate: string;
  showBookedDate: string;
  email: string;
  showDownloadBtn: boolean;
  event: { name: string; image: string; venue: string; performanceDate: string };
  customer: { name: string };
  seats: {
    ticketCount: number;
    ticketCountDisplay: string;
    display: string;
    area: string;
    viewBookingSeats: string;
    seats: string;
  };
  tickets: ShowDetailTicket[];
  userCurrency: string;
  usercurrencySymbol: string;
  totalPriceInUserCurrency: string;
  theatreName: string;
  theareId: number;
  eventMinimumAge?: string;
  hasMinimumAge: boolean;
  eventRunningTime?: string;
  specialNote?: string;
  hasSpecialNote: boolean;
}

export interface ShowBookingDetailResponse {
  status: number;
  message: string;
  data: ShowBookingDetailData | null;
}

/** One ticket payload entry the client must build before requesting a PDF — `image` is the
 *  base64 PNG (no `data:` prefix) rendered client-side from the ticket's own barcode value. */
export interface ShowETicketPayload {
  area: string;
  TicketId: number;
  seat: string;
  barcode: string;
  barcodeType: string;
  image: string;
}

/**
 * Native Shows (theatre) bookings client — calls `sports-events-service` directly (Shows now
 * lives inside that service, see `services/sports-events-service/src/modules/shows/`; 2026-08-06),
 * NOT the CMS. Same base URL as `sportsEventsClient`/`concertsEventsClient` (`NEXT_PUBLIC_
 * SPORTS_EVENTS_API_URL`) — no CORS/session issues, no partner API key, since this is now one of
 * our own backends. Replaces the earlier CMS-partner-API-proxy design (three Next.js routes under
 * `app/api/integration/{get-my-theater-bookings,get-booking-detail,download-e-ticket}` — removed).
 */
export class ShowsClient extends BaseApiClient {
  constructor(baseUrl?: string) {
    super(baseUrl ?? process.env.NEXT_PUBLIC_SPORTS_EVENTS_API_URL);
  }

  /**
   * Send the `member_d` session cookie on every Shows call so the member-scoped bookings list can
   * resolve the memberId server-side (`MemberSessionService`), same convention as `HotelsClient`.
   */
  protected override async request<T>(
    endpoint: string,
    options?: RequestInit & { next?: { revalidate?: number | false; tags?: string[] }; timeoutMs?: number },
  ): Promise<T> {
    return super.request<T>(endpoint, { ...(options || {}), credentials: 'include' });
  }

  /** Member-scoped bookings list (manage-bookings "Shows" tab) — memberId is resolved server-side
   *  from the session cookie, not sent by the client. */
  async getMyTheaterBookings(params: { page?: number; pagesize?: number } = {}): Promise<ShowsBookingsListResponse> {
    return this.get<ShowsBookingsListResponse>('/shows/bookings/list', {
      ...(params.page ? { page: params.page } : {}),
      ...(params.pagesize ? { pagesize: params.pagesize } : {}),
    });
  }

  /** Booking detail for the manage-bookings Shows detail page — gated only by the order's own
   *  `_id` (`cartId` here, matching the frontend's existing query-param naming), no memberId. */
  async getBookingDetail(cartId: string): Promise<ShowBookingDetailResponse> {
    return this.get<ShowBookingDetailResponse>(`/shows/bookings/${encodeURIComponent(cartId)}`);
  }

  /**
   * Requests the PDF e-ticket for a confirmed booking. The caller must have already rendered each
   * ticket's barcode/QR to a canvas and built `tickets` (see `ShowETicketPayload`) — this method
   * POSTs that payload and returns the resulting PDF as a `Blob`. Not routed through `request()`
   * (which always parses JSON) since the response body here is binary.
   */
  async downloadETicket(params: {
    cartId: string;
    tickets: ShowETicketPayload[];
    specialNoteMessage?: string;
  }): Promise<Blob> {
    if (!this.baseUrl) {
      throw new Error(`${this.constructor.name}: base URL is not configured. Set NEXT_PUBLIC_SPORTS_EVENTS_API_URL.`);
    }
    const response = await fetch(`${this.baseUrl}/shows/bookings/${encodeURIComponent(params.cartId)}/e-ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cartId: params.cartId,
        tickets: params.tickets,
        specialNoteMessage: params.specialNoteMessage ?? '',
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to download e-ticket');
    }
    return response.blob();
  }
}

export const showsClient = new ShowsClient();
